import React from 'react'
import { Tabs, Tab, Box, useTheme, useMediaQuery } from '@mui/material'
import {
  Person,
  Work,
  School,
  Settings,
  Description,
  FavoriteBorder,
  Security,
  Assessment,
} from '@mui/icons-material'

/**
 * ProfileTabs - Modern tabbed navigation for profile sections
 * Features:
 * - Icon + label tabs
 * - Scrollable on mobile
 * - Active tab highlighting
 * - Smooth transitions
 * - Responsive design
 */
const ProfileTabs = ({
  activeTab,
  onChange,
  tabs, // Array of { label, icon, id, badge? }
}) => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))

  // Find the index of the active tab
  const activeIndex = tabs.findIndex((tab) => tab.id === activeTab)

  const handleChange = (event, newIndex) => {
    const newTab = tabs[newIndex]
    if (onChange && newTab) {
      onChange(newTab.id)
    }
  }

  return (
    <Box
      sx={{
        borderBottom: 1,
        borderColor: 'divider',
        mb: 4,
        bgcolor: 'background.paper',
        position: 'sticky',
        top: 64, // Account for header height
        zIndex: 10,
      }}
    >
      <Tabs
        value={activeIndex >= 0 ? activeIndex : 0}
        onChange={handleChange}
        variant={isMobile ? 'scrollable' : 'standard'}
        scrollButtons={isMobile ? 'auto' : false}
        sx={{
          '& .MuiTab-root': {
            minHeight: 64,
            textTransform: 'none',
            fontSize: '1rem',
            fontWeight: 500,
            color: theme.palette.text.secondary,
            transition: 'all 0.2s',
            '&:hover': {
              color: theme.palette.primary.main,
              bgcolor: theme.palette.action.hover,
            },
            '&.Mui-selected': {
              color: theme.palette.primary.main,
              fontWeight: 600,
            },
          },
          '& .MuiTabs-indicator': {
            height: 3,
            borderRadius: '3px 3px 0 0',
            backgroundColor: theme.palette.primary.main,
          },
          '& .MuiTabs-scrollButtons': {
            color: theme.palette.primary.main,
          },
        }}
      >
        {tabs.map((tab) => (
          <Tab
            key={tab.id}
            label={tab.label}
            icon={tab.icon}
            iconPosition="start"
            id={`profile-tab-${tab.id}`}
            aria-controls={`profile-tabpanel-${tab.id}`}
          />
        ))}
      </Tabs>
    </Box>
  )
}

/**
 * Default tab configurations for candidate profiles
 */
export const defaultCandidateTabs = [
  { label: 'Overview', icon: <Person />, id: 'overview' },
  { label: 'Experience', icon: <Work />, id: 'experience' },
  { label: 'Credentials', icon: <School />, id: 'credentials' },
  { label: 'Documents', icon: <Description />, id: 'documents' },
  { label: 'Preferences', icon: <FavoriteBorder />, id: 'preferences' },
  { label: 'Activity', icon: <Assessment />, id: 'activity' },
  { label: 'Settings', icon: <Settings />, id: 'settings' },
]

/**
 * Default tab configurations for employer profiles
 */
export const defaultEmployerTabs = [
  { label: 'Overview', icon: <Person />, id: 'overview' },
  { label: 'About', icon: <Description />, id: 'about' },
  { label: 'Security', icon: <Security />, id: 'security' },
  { label: 'Settings', icon: <Settings />, id: 'settings' },
]

export default ProfileTabs
